/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Tenant Data Service - SuperAdmin purge / backup / restore API
 * ============================================================================
 *
 * Backend: TenantDataAdminController at /api/v1/admin/tenant-data.
 * Every JSON endpoint returns ApiResponse<T>; the job list returns
 * ApiResponse<PagedResponse<TenantDataJobDto>> (flat page object under `data`).
 *
 * ============================================================================
 */

import apiClient, { get, post, del } from '@/services/api/client';
import { ApiResponse, PaginatedResponse } from '@/services/api/types';
import { isApiError } from '@/services/api/errors';
import type {
  PurgePreview,
  PurgePreviewRequest,
  PurgeRequest,
  RestorePreview,
  RestoreRequest,
  RestoreUploadResult,
  TenantDataJob,
  TenantDataJobsParams,
} from '@/types/tenant-data.types';

const BASE_URL = '/admin/tenant-data';

/** Flat page object produced by core.common.dto.PagedResponse. */
interface BackendPagedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first?: boolean;
  last?: boolean;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

/** Extracts the filename from a Content-Disposition header, preferring RFC 5987 filename*. */
function filenameFromContentDisposition(header: unknown): string | null {
  if (typeof header !== 'string' || header.length === 0) return null;
  const extended = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (extended) {
    try {
      return decodeURIComponent(extended[1].trim());
    } catch {
      // fall through to the plain filename parameter
    }
  }
  const plain = /filename="([^"]+)"/i.exec(header);
  return plain ? plain[1] : null;
}

export const tenantDataService = {
  async previewPurge(req: PurgePreviewRequest): Promise<PurgePreview> {
    const response = await post<ApiResponse<PurgePreview>>(`${BASE_URL}/preview`, req);
    return response.data;
  },

  async startBackup(orgId: string): Promise<TenantDataJob> {
    const response = await post<ApiResponse<TenantDataJob>>(`${BASE_URL}/backup`, { orgId });
    return response.data;
  },

  async startPurge(req: PurgeRequest): Promise<TenantDataJob> {
    const response = await post<ApiResponse<TenantDataJob>>(`${BASE_URL}/purge`, req);
    return response.data;
  },

  /**
   * Raw upload of a backup ZIP: the File is the request body with Content-Type application/octet-stream
   * (not multipart, so the backend's global 10MB multipart cap does not apply; the service enforces 512 MiB).
   * No timeout: files can be hundreds of MB.
   */
  async uploadBackup(file: File): Promise<RestoreUploadResult> {
    const response = await post<ApiResponse<RestoreUploadResult>>(`${BASE_URL}/restore/upload`, file, {
      timeout: 0,
      headers: { 'Content-Type': 'application/octet-stream' },
    });
    return response.data;
  },

  async previewRestore(req: RestoreRequest): Promise<RestorePreview> {
    const response = await post<ApiResponse<RestorePreview>>(`${BASE_URL}/restore/preview`, req);
    return response.data;
  },

  async startRestore(req: RestoreRequest): Promise<TenantDataJob> {
    const response = await post<ApiResponse<TenantDataJob>>(`${BASE_URL}/restore`, req);
    return response.data;
  },

  async listJobs(params: TenantDataJobsParams): Promise<PaginatedResponse<TenantDataJob>> {
    const query: Record<string, unknown> = { page: params.page, size: params.size };
    if (params.orgId) {
      query.orgId = params.orgId;
    }
    const response = await get<ApiResponse<BackendPagedResponse<TenantDataJob>>>(
      `${BASE_URL}/jobs`,
      query
    );
    const paged = response.data;
    return {
      content: paged.content ?? [],
      page: {
        size: paged.size,
        number: paged.page,
        totalElements: paged.totalElements,
        totalPages: paged.totalPages,
      },
    };
  },

  async getJob(id: string): Promise<TenantDataJob> {
    const response = await get<ApiResponse<TenantDataJob>>(`${BASE_URL}/jobs/${id}`);
    return response.data;
  },

  /**
   * Streams the backup ZIP and triggers a browser download.
   * The API client interceptor drops blob error bodies, so a 404 arrives as
   * UNKNOWN_ERROR; it is normalized to BACKUP_NOT_AVAILABLE for translation.
   */
  async downloadBackup(id: string, fallbackName: string): Promise<void> {
    const response = await apiClient
      .get<Blob>(`${BASE_URL}/jobs/${id}/backup`, { responseType: 'blob', timeout: 0 })
      .catch((err: unknown) => {
        if (isApiError(err) && err.status === 404 && err.code === 'UNKNOWN_ERROR') {
          throw { ...err, code: 'BACKUP_NOT_AVAILABLE' };
        }
        throw err;
      });

    const fileName =
      filenameFromContentDisposition(response.headers['content-disposition']) ?? fallbackName;
    const blob = new Blob([response.data], { type: 'application/zip' });
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    setTimeout(() => URL.revokeObjectURL(href), 100);
  },

  async deleteBackup(id: string): Promise<void> {
    await del<ApiResponse<null>>(`${BASE_URL}/jobs/${id}/backup`);
  },
};

export default tenantDataService;
